import { Container, Graphics } from 'pixi.js';

/**
 * A dropped item in the world that the player can pick up.
 * Renders as a small colored square that bobs up and down.
 */
export class ItemDrop {
  /**
   * @param {number} x - World X position (pixels)
   * @param {number} y - World Y position (pixels)
   * @param {object} itemData - The item data (from items.json entry)
   * @param {string} itemId - The item key (e.g. 'rusty_sword', 'health_potion')
   */
  /**
   * @param {number} x - World X position (pixels)
   * @param {number} y - World Y position (pixels)
   * @param {object} itemData - The item data (from items.json entry)
   * @param {string} itemId - The item key (e.g. 'rusty_sword', 'health_potion')
   * @param {object} [options] - Optional settings
   * @param {number} [options.pickupDelay=0] - Seconds before item can be picked up
   */
  constructor(x, y, itemData, itemId, options = {}) {
    this.x = x;
    this.y = y;
    this.itemData = itemData;
    this.itemId = itemId;
    this.bobTimer = Math.random() * Math.PI * 2; // offset so not all items bob in sync
    this.baseY = y;
    this.pickupImmunity = options.pickupDelay || 0; // seconds until pickupable
    this.container = new Container();
    this.container.x = Math.round(x);
    this.container.y = Math.round(y);
    this.gfx = null;
    this.createGraphics();
  }

  /**
   * Draw the item as a small colored square (8x8).
   */
  createGraphics() {
    this.gfx = new Graphics();
    const color = parseInt(this.itemData.color, 16) || 0xffffff;
    // Center the 8x8 square
    this.gfx.rect(-4, -4, 8, 8);
    this.gfx.fill(color);
    this.container.addChild(this.gfx);
  }

  /**
   * Update the bobbing animation.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    // Tick pickup immunity
    if (this.pickupImmunity > 0) {
      this.pickupImmunity -= dt;
      // Flash alpha while immune
      this.container.alpha = 0.4 + Math.sin(this.bobTimer * 8) * 0.3;
    } else {
      this.container.alpha = 1.0;
    }

    this.bobTimer += dt * Math.PI * 4; // 2 Hz
    const bobOffset = Math.sin(this.bobTimer) * 2; // 2px amplitude
    this.container.y = Math.round(this.baseY + bobOffset);
  }

  /**
   * Check if this item is within pickup range of the player.
   * @param {number} playerX - Player X position (top-left)
   * @param {number} playerY - Player Y position (top-left)
   * @param {number} [pickupRange=20] - Distance threshold in pixels
   * @returns {boolean}
   */
  isNearPlayer(playerX, playerY, pickupRange = 20) {
    // Can't pick up while immune
    if (this.pickupImmunity > 0) return false;

    // Player center (assuming TILE_SIZE=16, player is centered in tile)
    const pcx = playerX + 8;
    const pcy = playerY + 8;
    const dx = pcx - this.x;
    const dy = pcy - this.baseY;
    return (dx * dx + dy * dy) <= (pickupRange * pickupRange);
  }
}
